<template lang="pug">
.multi-select-dropdown.relative
  //- Label
  label.block.mb-2(v-if="label" :for="inputId")
    | {{ label }}
    span.text-red-500.required-asterisk(v-if="required") &nbsp;*
  
  //- Selected items as chips
  .selected-chips.mb-2(v-if="selectedItems.length > 0")
    .flex.flex-wrap.gap-2
      .chip.flex.items-center.gap-1.px-3.py-1.bg-blue-700.text-white.rounded-full.text-sm.transition-all.duration-200(
        v-for="item in selectedItems" 
        :key="item"
      )
        span {{ item }}
        button.remove-chip.w-4.h-4.flex.items-center.justify-center.rounded-full.hover_bg-blue-600.transition-all.duration-200(
          @click="removeItem(item)"
          type="button"
          aria-label="Remove item"
        )
          Icon(icon="mdi:close" class="w-3 h-3")
  
  //- Dropdown trigger
  .dropdown-trigger.relative
    input.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded.text-gray-900.dark_text-white(
      :id="inputId"
      :value="searchQuery"
      @input="updateSearch"
      @focus="openDropdown"
      @keydown="handleKeydown"
      :placeholder="placeholder"
      :disabled="disabled"
      :class="{ 'opacity-50 cursor-not-allowed': disabled }"
      ref="inputRef"
    )
    button.dropdown-arrow.absolute.right-2.transform.p-1.transition-transform.duration-200(
      @click="toggleDropdown"
      type="button"
      :disabled="disabled"
      :class="{ 'opacity-50 cursor-not-allowed': disabled }"
      style="top: 50%; transform: translateY(-50%);"
    )
      Icon(
        :icon="isOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'" 
        class="w-4 h-4 text-gray-500"
      )
  
  //- Dropdown menu
  .dropdown-menu.absolute.top-full.left-0.right-0.mt-1.bg-gray-700.border.border-gray-600.rounded.shadow-lg.z-50.max-h-60.overflow-y-auto.dark_bg-gray-700.dark_border-gray-600.bg-white.border-gray-300(
    v-if="isOpen"
    ref="dropdownRef"
  )
    .p-2
      //- Search results or all options
      .option-list.space-y-1
        .option.flex.items-center.gap-2.p-2.rounded.cursor-pointer.transition-colors.duration-150.text-gray-900.dark_text-white.hover_bg-gray-100.hover_text-gray-900.dark_hover_bg-gray-600.dark_hover_text-white(
          v-for="option in filteredOptions" 
          :key="option"
          @click="toggleOption(option)"
          @mouseenter="highlightedIndex = filteredOptions.indexOf(option)"
          :class="{ 'bg-gray-100 text-gray-900 dark_bg-gray-600 dark_text-white': highlightedIndex === filteredOptions.indexOf(option), 'text-gray-900 dark_text-white': selectedItems.includes(option) }"
        )
          input.checkbox.w-4.h-4.text-primary-600.bg-gray-600.border-gray-500.rounded.focus_ring-primary-500(
            type="checkbox"
            :checked="selectedItems.includes(option)"
            @click.stop
            @change="toggleOption(option)"
          )
          span.text-sm {{ option }}
      
      //- No results message
      .no-results.p-2.text-sm.text-gray-400.dark_text-gray-400.text-gray-500.text-center(
        v-if="filteredOptions.length === 0"
      )
        | No options found
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { Icon } from '@iconify/vue';

// Props
interface Props {
  modelValue: string[] | string;
  options: string[];
  dataType: 'comma-separated' | 'array';
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Select options',
  label: '',
  required: false,
  disabled: false
});

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: string[] | string];
}>();

// Reactive state
const isOpen = ref(false);
const searchQuery = ref('');
const highlightedIndex = ref(-1);
const inputRef = ref<HTMLInputElement>();
const dropdownRef = ref<HTMLElement>();

// Generate unique ID for accessibility
const inputId = `multi-select-${Math.random().toString(36).substr(2, 9)}`;

// Normalize input value to array
const normalizeInput = (
  value: string[] | string,
  dataType: string
): string[] => {
  if (dataType === 'comma-separated' && typeof value === 'string') {
    if (!value) return [];
    return value
      .split(',')
      .map(item => item.trim())
      .filter(item => item);
  }
  return Array.isArray(value) ? value : [];
};

// Normalize output value based on dataType
const normalizeOutput = (
  value: string[],
  dataType: string
): string[] | string => {
  if (dataType === 'comma-separated') {
    return value.join(', ');
  }
  return value;
};

// Computed properties
const selectedItems = computed(() => {
  return normalizeInput(props.modelValue, props.dataType);
});

const filteredOptions = computed(() => {
  if (!searchQuery.value) {
    // Show all options when no search query
    return props.options;
  }
  return props.options.filter(option =>
    option.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

// Methods
const updateSearch = (event: Event) => {
  const target = event.target as HTMLInputElement;
  searchQuery.value = target.value;
  highlightedIndex.value = -1;
};

const openDropdown = () => {
  if (!props.disabled) {
    isOpen.value = true;
  }
};

const closeDropdown = () => {
  isOpen.value = false;
  searchQuery.value = '';
  highlightedIndex.value = -1;
};

const toggleDropdown = () => {
  if (isOpen.value) {
    closeDropdown();
  } else {
    openDropdown();
  }
};

const toggleOption = (option: string) => {
  const currentSelected = [...selectedItems.value];
  const index = currentSelected.indexOf(option);

  if (index > -1) {
    currentSelected.splice(index, 1);
  } else {
    currentSelected.push(option);
  }

  emit('update:modelValue', normalizeOutput(currentSelected, props.dataType));

  // Keep dropdown open
  if (!isOpen.value) {
    isOpen.value = true;
  }
};

const removeItem = (item: string) => {
  const currentSelected = selectedItems.value.filter(
    selected => selected !== item
  );
  emit('update:modelValue', normalizeOutput(currentSelected, props.dataType));

  // Keep dropdown open
  if (!isOpen.value) {
    isOpen.value = true;
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!isOpen.value) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openDropdown();
    }
    return;
  }

  switch (event.key) {
    case 'Escape':
      event.preventDefault();
      closeDropdown();
      inputRef.value?.blur();
      break;
    case 'ArrowDown':
      event.preventDefault();
      if (highlightedIndex.value < filteredOptions.value.length - 1) {
        highlightedIndex.value++;
      }
      break;
    case 'ArrowUp':
      event.preventDefault();
      if (highlightedIndex.value > 0) {
        highlightedIndex.value--;
      }
      break;
    case 'Enter':
      event.preventDefault();
      if (
        highlightedIndex.value >= 0 &&
        filteredOptions.value[highlightedIndex.value]
      ) {
        toggleOption(filteredOptions.value[highlightedIndex.value]);
      }
      break;
    case ' ':
      event.preventDefault();
      if (
        highlightedIndex.value >= 0 &&
        filteredOptions.value[highlightedIndex.value]
      ) {
        toggleOption(filteredOptions.value[highlightedIndex.value]);
      }
      break;
  }
};

// Watch for external changes to modelValue
watch(
  () => props.modelValue,
  () => {
    // Reset search when modelValue changes externally
    if (!isOpen.value) {
      searchQuery.value = '';
    }
  },
  { deep: true }
);

// Click outside to close
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node;
  if (
    dropdownRef.value &&
    !dropdownRef.value.contains(target) &&
    !inputRef.value?.contains(target)
  ) {
    closeDropdown();
  }
};

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.checkbox {
  accent-color: #3b82f6;
}
</style>
